/**
 * OraStallTap — v1.3 stalled-user re-activation card.
 *
 * Rendered ABOVE the normal recommendation triple when the backend's
 * /api/oracle/recommend response includes a non-null `stall_tap` field.
 * Visual weight is heavier than a normal rec card — this is the host
 * acknowledging "I noticed you were gone" without saying it that way.
 *
 * Lane discipline: stall taps NEVER mention tickers or market opinions.
 * They surface product paths only. Backend's lane_contract regex would
 * scrub a violation, but since the templates are static (no LLM in the
 * stall path), there's nothing to scrub.
 *
 * The T4 (final) tap renders softer — explicit "we'll stop reaching out"
 * tone. Honest contract — never run a 5th tap.
 *
 * Click semantics:
 *   - Posts a `stall_click` telemetry event so the backend cancels the
 *     current stall sequence (frees the user from further taps until
 *     they stall again).
 *   - Then navigates to cta_path.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/utils/api";

export default function OraStallTap({ tap, theme }) {
  const navigate = useNavigate();
  if (!tap) return null;

  function handleClick() {
    // Fire-and-forget cancellation — UI navigates immediately, no spinner.
    api.post('/oracle/telemetry', {
      event: "stall_click",
      rec_template_id: tap.template_id,
    }).catch(() => {});
    if (tap.cta_path) {
      if (tap.cta_path.startsWith("http")) {
        window.location.href = tap.cta_path;
      } else {
        navigate(tap.cta_path);
      }
    }
  }

  const isFinal = !!tap.is_final;
  // T4 uses a softer, less assertive palette so it doesn't feel pushy.
  const accent = theme?.primary || "#C9A227";
  const accentSoft = theme?.accent || "#E6C04A";

  return (
    <div
      data-testid="ora-stall-tap"
      data-tap-id={tap.tap_id}
      data-is-final={isFinal}
      style={{
        marginTop: 16,
        marginBottom: 16,
        padding: "18px 20px",
        background: isFinal
          ? "linear-gradient(140deg, rgba(245,242,233,0.04), rgba(245,242,233,0.02))"
          : `linear-gradient(140deg, ${accent}14, ${accentSoft}06)`,
        border: `1px solid ${isFinal ? "rgba(245,242,233,0.18)" : accent + "55"}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: isFinal ? (theme?.textSecondary || "#A8A294") : accent,
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        ORA · {isFinal ? "Final note" : "A nudge"}
      </div>
      <div
        style={{
          fontSize: "1.05rem",
          fontWeight: 700,
          color: theme?.textPrimary || "#F5F2E9",
          fontFamily: "Space Grotesk, sans-serif",
          marginBottom: 6,
          lineHeight: 1.3,
        }}
      >
        {tap.headline}
      </div>
      <div
        style={{
          fontSize: "0.86rem",
          color: theme?.textSecondary || "#A8A294",
          lineHeight: 1.55,
          marginBottom: 14,
        }}
      >
        {tap.body}
      </div>
      <button
        onClick={handleClick}
        data-testid={`ora-stall-tap-cta-${tap.template_id}`}
        style={{
          background: isFinal
            ? "transparent"
            : `linear-gradient(135deg, ${accent}, ${accentSoft})`,
          color: isFinal ? (theme?.textPrimary || "#F5F2E9") : "#0A0A0B",
          border: isFinal ? `1px solid ${accent}55` : "none",
          borderRadius: 10,
          padding: "10px 16px",
          fontWeight: 700,
          fontSize: "0.86rem",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {tap.cta_text}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
